import React, { useMemo, useState, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { getCampaignReport } from '../../mailTemplate/src/services/B2BServices'
import { useToast } from '../hooks/useToast'

import {
  Box,
  Typography,
  Grid,
  Paper,
  Skeleton,
  IconButton
} from '@mui/material'
import {
  Inbox,
  Schedule,
  Send,
  Refresh
} from '@mui/icons-material'
import { PieChart } from '@mui/x-charts/PieChart'

const StatItem = ({ icon, title, value, color = 'primary.main', isLoading = false }) => (
  <Box sx={{ textAlign: 'center', p: 1.5 }}>
    <Box
      sx={{
        mb: 1,
        color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      {icon}
    </Box>
    <Typography
      variant="h5"
      component="div"
      sx={{
        fontWeight: 600,
        mb: 0.5,
        color: 'text.primary',
        display: 'flex',
        justifyContent: 'center'
      }}
    >
      {isLoading ? <Skeleton width={45} /> : (value !== null && value !== undefined ? value : '-')}
    </Typography>
    <Typography
      variant="body2"
      color="text.secondary"
      sx={{ fontWeight: 400 }}
    >
      {title}
    </Typography>
  </Box>
)

const PieChartSkeleton = ({ size = 320 }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
    <Skeleton variant="circular" width={size} height={size} />

    <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 2, maxWidth: 500 }}>
      {[...Array(4)].map((_, index) => (
        <Box
          key={index}
          sx={{
            display: 'flex',
            alignItems: 'center',
            p: 1,
            borderRadius: 1
          }}
        >
          <Skeleton variant="circular" width={12} height={12} sx={{ mr: 1 }} />
          <Skeleton width={60} height={14} sx={{ mr: 1 }} />
          <Skeleton width={20} height={14} sx={{ mr: 0.5 }} />
          <Skeleton width={35} height={12} />
        </Box>
      ))}
    </Box>
  </Box>
)

const EmptyPieChart = ({ size = 320 }) => {
  const { t } = useTranslation()

  const emptyData = [{
    id: 0,
    value: 1,
    label: '',
    color: '#e0e0e0'
  }]

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3, py: 4 }}>
      <Box sx={{ position: 'relative' }}>
        <PieChart
          series={[
            {
              data: emptyData,
              cx: size / 2,
              cy: size / 2,
              outerRadius: size / 2 - 20,
              color: '#e0e0e0'
            }
          ]}
          colors={['#e0e0e0']}
          width={size}
          height={size}
          slotProps={{
            legend: { hidden: true },
            pieArc: {
              style: {
                pointerEvents: 'none',
                fill: '#e0e0e0'
              }
            }
          }}
          sx={{
            '& .MuiPieArc-root': {
              fill: '#e0e0e0 !important'
            }
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            zIndex: 1
          }}
        >
          <Typography variant="body2" color="text.primary" sx={{ fontWeight: 500 }}>
            {t('mailTemplate.campaigns.detail.noEmailStatsAvailable')}
          </Typography>
        </Box>
      </Box>
    </Box>
  )
}

export const EmailStatsPieChart = ({ data, size = 320 }) => {
  const colors = {
    WAITING: 'rgb(205, 205, 202)',
    FAILED: 'rgb(170, 108, 99)',
    DELIVERED: 'rgb(95, 125, 165)',
    OPENED: 'rgb(166, 172, 108)',
    CLICKED: 'rgb(120, 150, 100)',
    REJECTED: 'rgb(130, 75, 70)'
  }

  const total = data.reduce((sum, item) => sum + item.value, 0)

  const chartData = data.map((item, index) => {
    const percentage = (item.value / total) * 100
    return {
      id: index,
      value: item.value,
      label: item.category,
      color: colors[item.key] || '#666',
      percentage
    }
  })

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 3 }}>
      <PieChart
        series={[
          {
            cx: size / 2,
            cy: size / 2,
            data: chartData,
            arcLabelMinAngle: 10,
            outerRadius: size / 2 - 20,
            highlightScope: { faded: 'global', highlighted: 'item' },
            arcLabel: (item) => item.percentage >= 10 ? item.label : '',
            faded: { innerRadius: 30, additionalRadius: -30, color: 'gray' }
          }
        ]}
        width={size}
        height={size}
        slotProps={{
          legend: { hidden: true }
        }}
      />

      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 2, maxWidth: 500 }}>
        {data.map((item, index) => {
          const percentage = ((item.value / total) * 100).toFixed(1)

          return (
            <Box
              key={index}
              sx={{
                display: 'flex',
                alignItems: 'center',
                p: 1,
                borderRadius: 1
              }}
            >
              <Box
                sx={{
                  width: 12,
                  height: 12,
                  backgroundColor: colors[item.key] || '#666',
                  borderRadius: '50%',
                  mr: 1
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  mr: 1,
                  fontSize: '0.875rem'
                }}
              >
                {item.category}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 'bold',
                  mr: 0.5,
                  fontSize: '0.875rem'
                }}
              >
                {item.value}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  fontSize: '0.75rem'
                }}
              >
                ({percentage}%)
              </Typography>
            </Box>
          )
        })}
      </Box>
    </Box>
  )
}

const CampaignStats = ({ campaign }) => {
  const { t } = useTranslation()

  const { showError } = useToast()

  const [statsData, setStatsData] = useState(null)
  const [isLoadingStats, setIsLoadingStats] = useState(false)

  const formatDate = (dateString) => {
    if (isLoadingStats) return null

    if (!dateString) return t('mailTemplate.campaigns.detail.neverChecked')

    return new Date(dateString).toLocaleDateString()
  }

  const sentEmailCount = useMemo(() => {
    if (!statsData?.outgoingEmailStats) return null

    const stats = statsData.outgoingEmailStats
    const total = Object.values(stats).reduce((sum, count) => sum + (count || 0), 0)

    return total
  }, [statsData])

  const incomingEmailCount = useMemo(() => {
    if (!statsData) return null

    const count = statsData.incomingEmailCount

    return (count !== null && count !== undefined) ? count : null
  }, [statsData])

  const hasEmailStats = useMemo(() => {
    if (isLoadingStats) return true

    const stats = statsData?.outgoingEmailStats || {}

    return Object.values(stats).some(value => value > 0)
  }, [isLoadingStats, statsData?.outgoingEmailStats])

  const fetchStatsData = useCallback(async () => {
    if (!campaign?.id) return

    try {
      setIsLoadingStats(true)
      const reportResponse = await getCampaignReport(campaign.id)
      setStatsData(reportResponse.data)
    } catch (error) {
      showError(t('mailTemplate.campaigns.errors.failedToLoadCampaignStats'))
    } finally {
      setIsLoadingStats(false)
    }
  }, [campaign?.id, showError, t])

  const prepareChartData = () => {
    const stats = statsData?.outgoingEmailStats || {}

    return Object.entries(stats)
      .filter(([_, value]) => value > 0)
      .map(([key, value]) => ({
        category: t(`mailTemplate.campaigns.detail.emailStatus.${key}`),
        value,
        key
      }))
  }

  useEffect(() => {
    if (campaign?.id && !statsData && !isLoadingStats) {
      fetchStatsData()
    }
  }, [campaign?.id, statsData, isLoadingStats, fetchStatsData])

  return (
    <Box sx={{ py: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary' }}>
          {t('mailTemplate.campaigns.detail.campaignStatistics')}
        </Typography>
        <IconButton
          onClick={fetchStatsData}
          disabled={isLoadingStats}
          sx={{
            color: 'primary.main',
            '&:hover': {
              backgroundColor: 'rgba(33, 150, 243, 0.1)'
            }
          }}
        >
          <Refresh />
        </IconButton>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={4}>
          <Paper
            elevation={0}
            sx={{
              backgroundColor: 'rgba(33, 150, 243, 0.1)',
              borderRadius: 3,
              border: '1px solid rgba(33, 150, 243, 0.2)'
            }}
          >
            <StatItem
              icon={<Send sx={{ fontSize: 36 }} />}
              title={t('mailTemplate.campaigns.detail.sentEmails')}
              value={sentEmailCount}
              color="primary.main"
              isLoading={isLoadingStats}
            />
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Paper
            elevation={0}
            sx={{
              backgroundColor: 'rgba(76, 175, 80, 0.1)',
              borderRadius: 3,
              border: '1px solid rgba(76, 175, 80, 0.2)'
            }}
          >
            <StatItem
              icon={<Inbox sx={{ fontSize: 36 }} />}
              title={t('mailTemplate.campaigns.detail.incomingEmails')}
              value={incomingEmailCount}
              color="success.main"
              isLoading={isLoadingStats}
            />
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} md={4}>
          <Paper
            elevation={0}
            sx={{
              backgroundColor: 'rgba(255, 152, 0, 0.1)',
              borderRadius: 3,
              border: '1px solid rgba(255, 152, 0, 0.2)'
            }}
          >
            <StatItem
              icon={<Schedule sx={{ fontSize: 36 }} />}
              title={t('mailTemplate.campaigns.detail.lastInboxCheck')}
              value={formatDate(statsData?.inboxLastChecked)}
              color="warning.main"
              isLoading={isLoadingStats}
            />
          </Paper>
        </Grid>
      </Grid>

      <Box sx={{ mt: 6 }}>
        <Typography variant="h5" sx={{ mb: 3, fontWeight: 600, color: 'text.primary' }}>
          {t('mailTemplate.campaigns.detail.emailStatusDistribution')}
        </Typography>

        <Paper
          elevation={0}
          sx={{
            p: 3,
            backgroundColor: 'rgba(245, 245, 245, 0.3)',
            borderRadius: 3,
            border: '1px solid rgba(0, 0, 0, 0.1)'
          }}
        >
          {isLoadingStats
            ? <PieChartSkeleton />
            : hasEmailStats
              ? <EmailStatsPieChart data={prepareChartData()} />
              : <EmptyPieChart />
          }
        </Paper>
      </Box>

    </Box>
  )
}

export default CampaignStats
